import type { ReactNode } from 'react'
import { Stack, Typography } from '@mui/material'

import { ConfirmSettingModal } from 'components'

import {
  RESPEND_CONFIRM_LABEL,
  RESPEND_SKIP_LABEL,
  RESPEND_TITLE,
  respendConsequence,
  respendPreamble,
  respendSummary,
  sectionName,
  type RespendPlan,
} from './narrationRespend'

interface NarrationRespendModalProps {
  open: boolean
  articleTitle: string
  /** From `planRespend(narrated, edited)`. Open this only when `plan.needsConfirmation`. */
  plan: RespendPlan | null
  /** The ONLY path to a paid request: save, then queue re-narration of `plan.changedSections`. */
  onRenarrate: () => void
  /** Save the text and leave the narration stale (the website hides the player until re-narrated). */
  onSaveWithoutRenarrating: () => void
  /** Don't save. */
  onCancel: () => void
  loading?: boolean
  /** Who may approve spend is a caller decision (§46: disabled with a reason, never hidden). */
  renarrateDisabled?: boolean
  renarrateTooltip?: ReactNode
  /** A failure from the save or the queueing, shown under the consequence. */
  error?: string | null
}

/**
 * VRG-739 — the save gate for an edit to a NARRATED article (Spencer, 2026-10-23: "on save we
 * confirm the re-spend of narrating the edited sections").
 *
 * §43: the save is a Save; THIS is the consequence gate, because re-narrating spends ElevenLabs
 * credits and that cannot be taken back. It names the sections and the cost before anything is
 * sent, and offers the free alternative (save, narration stale) as its own button so that
 * Cancel keeps meaning "don't save".
 *
 * The component computes nothing and calls nothing: the plan arrives as a prop (see
 * `planRespend`), and the three outcomes are callbacks. No request is made from here.
 *
 * NOT WIRED YET: vera-2 has no article text editor (articles are TypeScript content in
 * vera-website; GTM → Articles approves and publishes but does not edit). The save flow that
 * opens this, and the job that re-narrates, are VRG-739's open half.
 */
export const NarrationRespendModal = ({
  open,
  articleTitle,
  plan,
  onRenarrate,
  onSaveWithoutRenarrating,
  onCancel,
  loading = false,
  renarrateDisabled = false,
  renarrateTooltip,
  error,
}: NarrationRespendModalProps) => (
  <ConfirmSettingModal
    open={open && !!plan}
    onClose={onCancel}
    onConfirm={onRenarrate}
    title={RESPEND_TITLE}
    preamble={plan ? respendPreamble(articleTitle, plan) : undefined}
    consequence={
      plan ? (
        <>
          <strong>{respendSummary(plan)}</strong>
          <br />
          {respendConsequence(plan)}
        </>
      ) : (
        ''
      )
    }
    confirmLabel={RESPEND_CONFIRM_LABEL}
    secondaryAction={{ label: RESPEND_SKIP_LABEL, onClick: onSaveWithoutRenarrating }}
    loading={loading}
    confirmDisabled={renarrateDisabled}
    confirmTooltip={renarrateTooltip}
    // Three buttons do not fit the default 533px on one row.
    sx={{ width: 640 }}
  >
    {plan && (
      <Stack spacing={1.5}>
        <Typography variant="body2" fontWeight={600}>
          {plan.changedSections.length === 1 ? 'Section to re-narrate' : 'Sections to re-narrate'}
        </Typography>
        <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
          {plan.changedSections.map((s) => (
            <Typography component="li" variant="body2" key={s.index}>
              {sectionName(s.label)}
              <Typography component="span" variant="body2" color="text.secondary">
                {` · ${s.credits.toLocaleString('en-US')} credits`}
              </Typography>
            </Typography>
          ))}
        </Stack>
        {plan.removedSections.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {`Removed from the narration, at no cost: ${plan.removedSections.map(sectionName).join(', ')}.`}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    )}
  </ConfirmSettingModal>
)
